import { FormApi } from 'final-form'
import React from 'react'
import { Field, Form } from 'react-final-form'

import { Outline } from '@cinch/oxd-components/button/src'
import Select from '@cinch/oxd-components/field-select/src'
import ResetOnChange from '@cinch/oxd-components/final-form-reset-on-change/src'
import Panel from '@cinch/oxd-components/panel/src'
import requestStatus from '@cinch/oxd-utils/request-status/src'
import { required } from '@cinch/oxd-utils/validators/src'

import { DamageItem } from '../../../types'
import useDamage from '../damage.hook'
import { DamageSection } from '../types'
import FetchOnChange from './fetch-on-change.component'
import config from './field-config'

type DamageFormData = { [key in DamageSection]: string }
type PartialDamageItem = Pick<
  DamageItem,
  Exclude<keyof DamageItem, 'valuationId' | 'valuationGuid' | 'quoteId' | 'quoteGuid' | 'lastUpdated'>
>

export interface DamageComponentProps {
  onAddDamage: (formData: PartialDamageItem) => any
}

/**
 * @link https://medium.com/@erikras/declarative-form-rules-c5949ea97366
 */
const DamageFormComponent: React.FC<DamageComponentProps> = ({ onAddDamage }) => {
  const {
    zones,
    zoneStatus,
    components,
    componentStatus,
    faults,
    faultStatus,
    getComponents,
    getFaults,
    resetComponents,
    resetFaults,
    resetAll,
  } = useDamage()

  const getResetFunction = (key: DamageSection) => {
    switch (key) {
      case DamageSection.Component: {
        return resetComponents
      }
      case DamageSection.Fault: {
        return resetFaults
      }
    }
  }

  const fieldConfig = config.map((item) =>
    !item.shouldResetOnChange
      ? item
      : {
          ...item,
          shouldResetOnChange: item.shouldResetOnChange.map((fieldName) => ({
            fieldName,
            resetFunction: getResetFunction(fieldName),
          })),
        },
  )

  // first set the options
  const [zoneField, componentsField, faultsField] = fieldConfig
  zoneField.options = zones
  componentsField.options = components
  faultsField.options = faults

  // set the disable status for each field
  zoneField.disabled = zoneStatus !== requestStatus.SUCCESS
  componentsField.disabled = componentStatus !== requestStatus.SUCCESS
  faultsField.disabled = faultStatus !== requestStatus.SUCCESS || componentsField.disabled

  const fetchAction = (_name: any, value: string, field: DamageSection) => {
    if (!value) {
      return
    }

    if (field === DamageSection.Component) {
      getComponents(value)
    }

    if (field === DamageSection.Fault) {
      getFaults(value)
    }
  }

  const onSubmit = (formData: DamageFormData, form: FormApi) => {
    if (
      // eslint-disable-next-line no-prototype-builtins
      !formData.hasOwnProperty(DamageSection.Zone) ||
      // eslint-disable-next-line no-prototype-builtins
      !formData.hasOwnProperty(DamageSection.Component) ||
      // eslint-disable-next-line no-prototype-builtins
      !formData.hasOwnProperty(DamageSection.Fault) ||
      formData[DamageSection.Zone] === '' ||
      formData[DamageSection.Component] === '' ||
      formData[DamageSection.Fault] === ''
    ) {
      return
    }

    const output: PartialDamageItem = {
      damageId: 0,
      vehicleZoneId: parseInt(formData[DamageSection.Zone], 10),
      vehicleZoneName: zones.find((zone) => zone.value === formData[DamageSection.Zone])!.label,
      componentId: parseInt(formData[DamageSection.Component], 10),
      componentName: components.find((component) => component.value === formData[DamageSection.Component])!.label,
      faultId: parseInt(formData[DamageSection.Fault], 10),
      faultName: faults.find((fault) => fault.value === formData[DamageSection.Fault])!.label,
    }

    onAddDamage(output)

    form.change(DamageSection.Zone, '')
    form.change(DamageSection.Component, '')
    form.change(DamageSection.Fault, '')

    resetAll()
  }

  return (
    <Panel title="Vehicle Damage">
      <Form
        onSubmit={onSubmit as any}
        render={({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit} name="selectDamage">
            {fieldConfig.map(
              ({ name, label, options, disabled, placeholder, shouldResetOnChange, shouldFetchOnChange }) => (
                <React.Fragment key={name}>
                  <Field
                    name={name}
                    subscription={{ value: true }}
                    validate={required}
                    render={(props) => (
                      <Select
                        id={props.input.name}
                        options={options}
                        disabled={disabled}
                        placeholder={placeholder}
                        label={label}
                        oxford
                        {...props}
                      />
                    )}
                  />
                  <ResetOnChange name={name} fields={shouldResetOnChange as any} />
                  <FetchOnChange name={name} fields={shouldFetchOnChange as any} action={fetchAction} />
                </React.Fragment>
              ),
            )}
            <Outline type="submit" disabled={submitting || invalid} isLoading={submitting}>
              Add Damage
            </Outline>
          </form>
        )}
      />
    </Panel>
  )
}

export default DamageFormComponent
